package com.castly.admin.ui

import androidx.compose.runtime.*
import com.castly.admin.external.marked
import com.castly.admin.external.markedOptions
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLDivElement

@Composable
fun ApiDocsSection() {
    val colors = AppTheme.colors
    var markdownContent by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    val scope = rememberCoroutineScope()

    // Fetch the markdown file
    LaunchedEffect(Unit) {
        try {
            // Determine API base URL
            val apiBaseUrl = when {
                // Local development - frontend on 8081, backend on 8080
                window.location.port == "8081" -> "http://localhost:8080"
                // Staging environment - frontend on castly-stg.twistmena.com, backend on api-castly-stg.twistmena.com
                window.location.hostname.contains("castly-stg.twistmena.com") -> "https://api-castly-stg.twistmena.com"
                // Production environment - frontend on castly.twistmena.com, backend on api-castly.twistmena.com
                window.location.hostname.contains("castly.twistmena.com") -> "https://api-castly.twistmena.com"
                // Default: same origin
                else -> ""
            }

            val response = window.fetch("$apiBaseUrl/CLIENT_API_DOCUMENTATION.md").await()
            if (response.ok) {
                markdownContent = response.text().await()
            } else {
                error = "Failed to load documentation: ${response.statusText}"
            }
            loading = false
        } catch (e: Exception) {
            error = e.message ?: "Failed to load documentation"
            loading = false
        }
    }

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(1200.px)
            themeTransition()
        }
    }) {
        // Header with download buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.xxl)
                property("flex-wrap", "wrap")
                gap(Spacing.lg)
            }
        }) {
            H2({
                style {
                    marginTop(0.px)
                    marginBottom(0.px)
                    fontSize(Typography.xxl)
                    color(Color(colors.text))
                }
            }) { Text("API Documentation") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                }
            }) {
                // Determine API base URL for downloads
                val apiBaseUrl = when {
                    window.location.port == "8081" -> "http://localhost:8080"
                    window.location.hostname.contains("castly-stg.twistmena.com") -> "https://api-castly-stg.twistmena.com"
                    window.location.hostname.contains("castly.twistmena.com") -> "https://api-castly.twistmena.com"
                    else -> ""
                }

                // Download as Markdown button
                A(href = "$apiBaseUrl/CLIENT_API_DOCUMENTATION.md", attrs = {
                    attr("download", "castly-api-docs.md")
                    attr("target", "_blank")
                    style {
                        padding(10.px, Spacing.lg)
                        backgroundColor(Color(colors.primary))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight("600")
                        property("text-decoration", "none")
                        display(DisplayStyle.InlineBlock)
                    }
                }) {
                    Text("📄 Download Markdown")
                }

                // Download OpenAPI spec button
                A(href = "$apiBaseUrl/CASTLY_API_SPECIFICATION.yaml", attrs = {
                    attr("download", "castly-api-openapi.yaml")
                    attr("target", "_blank")
                    style {
                        padding(10.px, Spacing.lg)
                        backgroundColor(Color(colors.success))
                        color(Color("#ffffff"))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight("600")
                        property("text-decoration", "none")
                        display(DisplayStyle.InlineBlock)
                    }
                }) {
                    Text("🔌 Download OpenAPI/Swagger")
                }
            }
        }

        // Content area
        when {
            loading -> {
                Div({
                    style {
                        padding(40.px)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text("Loading documentation...")
                }
            }
            error != null -> {
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        borderRadius(BorderRadius.md)
                        color(Color(colors.danger))
                    }
                }) {
                    Text("Error: $error")
                }
            }
            markdownContent != null -> {
                // Display markdown content as HTML
                Div({
                    classes("markdown-content")
                    style {
                        lineHeight("1.6")
                        color(Color(colors.text))
                    }
                    ref { element ->
                        // Convert markdown to HTML using marked.js
                        try {
                            val options = markedOptions {
                                gfm = true  // GitHub Flavored Markdown
                                breaks = true  // Convert \n to <br>
                                headerIds = true  // Add IDs to headers
                            }
                            (element as? HTMLDivElement)?.innerHTML = marked.parse(markdownContent!!, options)
                        } catch (e: Exception) {
                            // Fallback to simple rendering if marked.js not available
                            (element as? HTMLDivElement)?.innerHTML = renderMarkdownFallback(markdownContent!!)
                        }
                        onDispose { }
                    }
                }) {}

                // Add CSS for markdown styling
                Style {
                    """
                    .markdown-content {
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .markdown-content h1 {
                        font-size: 2em;
                        font-weight: 700;
                        margin-top: 24px;
                        margin-bottom: 16px;
                        color: ${colors.text};
                        border-bottom: 2px solid ${colors.border};
                        padding-bottom: 8px;
                    }
                    .markdown-content h2 {
                        font-size: 1.5em;
                        font-weight: 700;
                        margin-top: 24px;
                        margin-bottom: 12px;
                        color: ${colors.text};
                    }
                    .markdown-content h3 {
                        font-size: 1.25em;
                        font-weight: 600;
                        margin-top: 20px;
                        margin-bottom: 10px;
                        color: ${colors.text};
                    }
                    .markdown-content p {
                        margin-bottom: 12px;
                    }
                    .markdown-content pre {
                        background-color: ${if (colors == LightColors) "#1f2937" else "#0d1117"};
                        color: ${colors.border};
                        padding: 16px;
                        border-radius: 6px;
                        overflow-x: auto;
                        margin-bottom: 16px;
                        font-size: 14px;
                        line-height: 1.5;
                        font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
                    }
                    .markdown-content code {
                        background-color: ${colors.surfaceHover};
                        color: #e11d48;
                        padding: 2px 6px;
                        border-radius: 3px;
                        font-size: 0.9em;
                        font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
                    }
                    .markdown-content pre code {
                        background-color: transparent;
                        color: ${colors.border};
                        padding: 0;
                        font-size: 14px;
                    }
                    .markdown-content strong {
                        font-weight: 700;
                        color: ${colors.text};
                    }
                    .markdown-content em {
                        font-style: italic;
                    }
                    .markdown-content table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 16px;
                    }
                    .markdown-content th {
                        background-color: ${colors.surfaceHover};
                        padding: 10px;
                        text-align: left;
                        border: 1px solid ${colors.border};
                        font-weight: 600;
                    }
                    .markdown-content td {
                        padding: 10px;
                        border: 1px solid ${colors.border};
                    }
                    .markdown-content ul, .markdown-content ol {
                        margin-bottom: 12px;
                        padding-left: 24px;
                    }
                    .markdown-content li {
                        margin-bottom: 6px;
                    }
                    .markdown-content a {
                        color: ${colors.primary};
                        text-decoration: none;
                    }
                    .markdown-content a:hover {
                        text-decoration: underline;
                    }
                    .markdown-content blockquote {
                        border-left: 4px solid ${colors.border};
                        padding-left: 16px;
                        margin: 16px 0;
                        color: ${colors.textMuted};
                        font-style: italic;
                    }
                    .markdown-content hr {
                        border: none;
                        border-top: 1px solid ${colors.border};
                        margin: 24px 0;
                    }
                    """.trimIndent()
                }
            }
        }
    }
}

/**
 * Fallback markdown renderer (used if marked.js is not available)
 * For production, use marked.js via CDN or npm
 */
private fun renderMarkdownFallback(markdown: String): String {
    var html = markdown

    // Code blocks (```language\ncode\n```)
    html = html.replace(Regex("```(\\w*)\n([\\s\\S]*?)\n```")) { match ->
        "<pre><code>${escapeHtml(match.groupValues[2])}</code></pre>"
    }

    // Inline code (`code`)
    html = html.replace(Regex("`([^`]+)`")) { match ->
        "<code>${escapeHtml(match.groupValues[1])}</code>"
    }

    // Headers
    html = html.replace(Regex("^### (.+)$", RegexOption.MULTILINE)) { "<h3>${it.groupValues[1]}</h3>" }
    html = html.replace(Regex("^## (.+)$", RegexOption.MULTILINE)) { "<h2>${it.groupValues[1]}</h2>" }
    html = html.replace(Regex("^# (.+)$", RegexOption.MULTILINE)) { "<h1>${it.groupValues[1]}</h1>" }

    // Bold (**text**)
    html = html.replace(Regex("\\*\\*(.+?)\\*\\*")) { "<strong>${it.groupValues[1]}</strong>" }

    // Italic (*text* or _text_)
    html = html.replace(Regex("\\*(.+?)\\*")) { "<em>${it.groupValues[1]}</em>" }
    html = html.replace(Regex("_(.+?)_")) { "<em>${it.groupValues[1]}</em>" }

    // Links [text](url)
    html = html.replace(Regex("\\[([^\\]]+)\\]\\(([^)]+)\\)")) {
        "<a href=\"${it.groupValues[2]}\" target=\"_blank\">${it.groupValues[1]}</a>"
    }

    // Horizontal rules
    html = html.replace(Regex("^---$", RegexOption.MULTILINE), "<hr>")

    // Tables (basic support)
    html = processMarkdownTables(html)

    // Paragraphs (double newlines)
    html = html.split("\n\n").joinToString("") { paragraph ->
        val trimmed = paragraph.trim()
        when {
            trimmed.startsWith("<h") || trimmed.startsWith("<pre") ||
            trimmed.startsWith("<table") || trimmed.startsWith("<hr") ||
            trimmed.startsWith("<ul") || trimmed.startsWith("<ol") -> trimmed
            trimmed.isNotEmpty() -> "<p>$trimmed</p>"
            else -> ""
        }
    }

    // Lists
    html = processLists(html)

    return html
}

private fun processMarkdownTables(text: String): String {
    val lines = text.split("\n")
    val result = StringBuilder()
    var inTable = false

    for (i in lines.indices) {
        val line = lines[i]

        // Check if this is a table row (contains pipes)
        if (line.trim().startsWith("|") && line.trim().endsWith("|")) {
            if (!inTable) {
                result.append("<table>")
                inTable = true
            }

            // Check if next line is separator (---|---|---)
            val isHeader = i + 1 < lines.size && lines[i + 1].contains(Regex("^\\|[\\s:-]+\\|"))

            val cells = line.split("|").drop(1).dropLast(1).map { it.trim() }

            if (isHeader) {
                result.append("<thead><tr>")
                cells.forEach { result.append("<th>${escapeHtml(it)}</th>") }
                result.append("</tr></thead><tbody>")
            } else if (!line.contains(Regex("^\\|[\\s:-]+\\|"))) {
                result.append("<tr>")
                cells.forEach { result.append("<td>${escapeHtml(it)}</td>") }
                result.append("</tr>")
            }
        } else {
            if (inTable) {
                result.append("</tbody></table>")
                inTable = false
            }
            result.append(line).append("\n")
        }
    }

    if (inTable) {
        result.append("</tbody></table>")
    }

    return result.toString()
}

private fun processLists(text: String): String {
    var html = text

    // Unordered lists (- item or * item)
    html = html.replace(Regex("((?:^[\\-\\*] .+$\n?)+)", RegexOption.MULTILINE)) { match ->
        val items = match.groupValues[1].trim().split("\n")
            .map { it.removePrefix("- ").removePrefix("* ").trim() }
            .joinToString("") { "<li>$it</li>" }
        "<ul>$items</ul>"
    }

    // Ordered lists (1. item)
    html = html.replace(Regex("((?:^\\d+\\. .+$\n?)+)", RegexOption.MULTILINE)) { match ->
        val items = match.groupValues[1].trim().split("\n")
            .map { it.replace(Regex("^\\d+\\.\\s+"), "").trim() }
            .joinToString("") { "<li>$it</li>" }
        "<ol>$items</ol>"
    }

    return html
}

private fun escapeHtml(text: String): String {
    return text
        .replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace("\"", "&quot;")
        .replace("'", "&#39;")
}
